import { useMemo, useCallback } from "react";
import { templateTokens } from "@utils";

const TOKEN_REGEX = /\{[^\s{}]+\}/g;
const ZWS = "\u200B";

/** Strip ASCII control characters (keep \n and \t). */
function sanitize(text) {
  // eslint-disable-next-line no-control-regex
  return text.replace(/[\x00-\x08\x0B\x0C\x0E-\x1F\x7F]/g, "");
}

/**
 * Returns true if a and b are the same inline formatting element
 * (same tag + same colour for span/font).
 */
function isSameFormat(a, b) {
  if (a.tagName !== b.tagName) return false;
  const tag = a.tagName.toLowerCase();
  if (tag === "span") return (a.style?.color || "") === (b.style?.color || "");
  if (tag === "font")
    return (a.getAttribute("color") || "") === (b.getAttribute("color") || "");
  return true; // b, strong, i, em, u — identical by tag name
}

function mergeAdjacentFormats(el) {
  const nodes = Array.from(el.childNodes);
  let i = 0;
  while (i < nodes.length) {
    const cur = nodes[i];
    if (cur.nodeType === Node.ELEMENT_NODE && cur.tagName.toLowerCase() !== "br") {
      mergeAdjacentFormats(cur); // depth-first
      // Advance j past text nodes that consist only of token patterns (e.g. {Name})
      let j = i + 1;
      while (j < nodes.length && nodes[j].nodeType === Node.TEXT_NODE) {
        const t = nodes[j].textContent;
        if (!/\{[^\s{}]+\}/.test(t)) break; // no token present
        if (t.split(/\{[^\s{}]+\}/).some((p) => p.trim())) break; // non-token text present
        j++;
      }
      // If a same-format sibling immediately follows the token run, merge it in
      if (
        j < nodes.length &&
        nodes[j].nodeType === Node.ELEMENT_NODE &&
        isSameFormat(cur, nodes[j])
      ) {
        for (let k = i + 1; k <= j; k++) {
          const n = nodes[k];
          if (n.nodeType === Node.TEXT_NODE) {
            cur.appendChild(n);
          } else {
            // Same-format element — absorb its children then remove the shell
            while (n.firstChild) cur.appendChild(n.firstChild);
            n.remove();
          }
        }
        nodes.splice(i + 1, j - i);
        continue; // re-check cur: there may be more siblings to merge
      }
    }
    i++;
  }
}

/**
 * Sanitize rich HTML to a strict whitelist of safe inline formatting tags and
 * attributes. Strips everything else — including event handlers, script tags,
 * and arbitrary attributes — before the value is serialized and sent to the
 * backend.
 *
 * Allowed tags : b, strong, i, em, u, span, font, br
 * Allowed attrs: span → style.color only; font → color attribute only
 */
const RICH_ALLOWED_TAGS = new Set([
  "b", "strong", "i", "em", "u", "span", "font", "br",
]);

function sanitizeRichHtml(html) {
  if (!html) return html;
  const doc = new DOMParser().parseFromString(html, "text/html");

  const walk = (node) => {
    Array.from(node.childNodes).forEach((child) => {
      if (child.nodeType === Node.TEXT_NODE) return;

      if (child.nodeType !== Node.ELEMENT_NODE) {
        child.parentNode.removeChild(child);
        return;
      }

      const tag = child.tagName.toLowerCase();

      if (!RICH_ALLOWED_TAGS.has(tag)) {
        // Unwrap: hoist children into the parent, then remove the element
        while (child.firstChild) child.parentNode.insertBefore(child.firstChild, child);
        child.parentNode.removeChild(child);
        return;
      }

      // Capture allowed colour value before stripping all attributes
      const color =
        (tag === "span" ? child.style?.color : null) ||
        (tag === "font" ? child.getAttribute("color") : null) ||
        null;

      // Strip every attribute
      while (child.attributes.length > 0) {
        child.removeAttribute(child.attributes[0].name);
      }

      // Re-apply only colour
      if (color) {
        if (tag === "span") child.style.color = color;
        else child.setAttribute("color", color);
      }

      walk(child);
    });
  };

  walk(doc.body);
  return doc.body.innerHTML;
}

export default function useTemplateTokens(blacklistedTokens) {
  const availableTokens = useMemo(
    () => templateTokens.filter((t) => !blacklistedTokens?.includes(t.value)),
    [blacklistedTokens],
  );

  const tokenMap = useMemo(
    () => new Map(availableTokens.map((t) => [t.value, t.name])),
    [availableTokens],
  );

  const validTokenSet = useMemo(
    () => new Set(availableTokens.map((t) => t.value)),
    [availableTokens],
  );

  const createTokenElement = useCallback(
    (tokenValue, displayName, isValid = true) => {
      const span = document.createElement("span");
      span.contentEditable = "false";
      span.dataset.token = tokenValue;
      span.className = isValid
        ? "inline-flex items-center bg-primary-100 text-primary-600 rounded-[0.3em] px-[0.3em] py-[0.1em] font-medium cursor-pointer"
        : "inline-flex items-center bg-red-100 text-red-600 rounded-[0.3em] px-[0.3em] py-[0.1em] font-medium cursor-pointer";
      span.textContent = displayName;
      return span;
    },
    [],
  );

  const serialize = useCallback((container) => {
    let result = "";
    const walk = (node) => {
      if (node.nodeType === Node.TEXT_NODE) {
        result += node.textContent.replace(new RegExp(ZWS, "g"), "");
      } else if (node.nodeType === Node.ELEMENT_NODE) {
        if (node.dataset?.token) {
          result += node.dataset.token;
        } else if (node.tagName === "BR") {
          result += "\n";
        } else {
          if (
            (node.tagName === "DIV" || node.tagName === "P") &&
            result.length > 0 &&
            !result.endsWith("\n")
          ) {
            result += "\n";
          }
          node.childNodes.forEach(walk);
        }
      }
    };
    container.childNodes.forEach(walk);
    return sanitize(result);
  }, []);

  const serializeRich = useCallback((container) => {
    const clone = container.cloneNode(true);
    // Strip zero-width spaces from text nodes
    const walker = document.createTreeWalker(clone, NodeFilter.SHOW_TEXT);
    const textNodes = [];
    let n;
    while ((n = walker.nextNode())) textNodes.push(n);
    textNodes.forEach((tn) => {
      tn.textContent = tn.textContent.replace(new RegExp(ZWS, "g"), "");
    });
    // Replace token spans with their {token_value} as plain text
    clone.querySelectorAll("[data-token]").forEach((span) => {
      span.parentNode.replaceChild(
        document.createTextNode(span.dataset.token),
        span,
      );
    });
    clone.normalize();
    mergeAdjacentFormats(clone);
    return sanitizeRichHtml(sanitize(clone.innerHTML));
  }, []);

  const deserialize = useCallback(
    (text) => {
      const fragment = document.createDocumentFragment();
      if (!text) return fragment;

      const parts = text.split(TOKEN_REGEX);
      const tokens = text.match(TOKEN_REGEX) || [];

      parts.forEach((part, i) => {
        if (part) {
          const lines = part.split("\n");
          lines.forEach((line, j) => {
            if (j > 0) fragment.appendChild(document.createElement("br"));
            if (line) fragment.appendChild(document.createTextNode(line));
          });
        } else if (tokens.length > 0) {
          fragment.appendChild(document.createTextNode(ZWS));
        }

        if (i < tokens.length) {
          const tokenValue = tokens[i];
          const displayName = tokenMap.get(tokenValue);
          const isValid = validTokenSet.has(tokenValue);
          fragment.appendChild(
            createTokenElement(tokenValue, displayName || tokenValue, isValid),
          );
        }
      });

      return fragment;
    },
    [tokenMap, validTokenSet, createTokenElement],
  );

  const deserializeRich = useCallback(
    (html) => {
      if (!html) return document.createDocumentFragment();
      // Fall back to plain-text deserialization for legacy plain values
      if (!/<[a-zA-Z]/.test(html)) return deserialize(html);

      const parser = new DOMParser();
      const doc = parser.parseFromString(html, "text/html");

      const processNode = (node) => {
        if (node.nodeType === Node.TEXT_NODE) {
          const text = node.textContent;
          const parts = text.split(TOKEN_REGEX);
          const tokens = text.match(TOKEN_REGEX) || [];
          if (!tokens.length) return [document.createTextNode(text)];
          const result = [];
          parts.forEach((part, i) => {
            if (part) result.push(document.createTextNode(part));
            if (i < tokens.length) {
              const tokenValue = tokens[i];
              const displayName = tokenMap.get(tokenValue);
              const isValid = validTokenSet.has(tokenValue);
              result.push(
                createTokenElement(
                  tokenValue,
                  displayName || tokenValue,
                  isValid,
                ),
              );
            }
          });
          return result;
        }
        if (node.nodeType === Node.ELEMENT_NODE) {
          const cloned = document.importNode(node, false);
          cloned.removeAttribute("contenteditable");
          node.childNodes.forEach((child) => {
            processNode(child).forEach((m) => cloned.appendChild(m));
          });
          return [cloned];
        }
        return [];
      };

      const fragment = document.createDocumentFragment();
      doc.body.childNodes.forEach((child) => {
        processNode(child).forEach((m) => fragment.appendChild(m));
      });
      return fragment;
    },
    [tokenMap, validTokenSet, createTokenElement, deserialize],
  );

  const validateTokens = useCallback(
    (text) => {
      if (!text) return true;
      const matches = text.match(TOKEN_REGEX);
      if (!matches) return true;
      const hasInvalid = matches.some((m) => !validTokenSet.has(m));
      return hasInvalid ? "Message contains invalid template tokens" : true;
    },
    [validTokenSet],
  );

  return {
    availableTokens,
    tokenMap,
    validTokenSet,
    serialize,
    deserialize,
    serializeRich,
    deserializeRich,
    createTokenElement,
    validateTokens,
  };
}
